import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { updateRestaurantSubscriptions } from "../../../redux/slices/userSlice";
import { toast } from "../../../utils/demoToast";
import useApi from "../../../hooks/useApi";
import Button from "../../buttons/Button";
import Alert from "../../utils/Alert";
import Spinner from "../../utils/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import {
  faTablePicnic,
  faCalendarCheck,
  faPuzzlePiece,
} from "@fortawesome/pro-duotone-svg-icons";
import {
  getAvailableModules,
  getRestaurantSubscriptions,
  createCheckoutSession,
  cancelSubscription,
  reactivateSubscription,
  createPortalSession,
} from "../../../services/billingService";

// Module icons mapping
const moduleIcons = {
  tables: faTablePicnic,
  booking: faCalendarCheck,
  default: faPuzzlePiece,
};

// CRA inlines NODE_ENV at build time (`npm run build` → "production").
// Do not rely on REACT_APP_ENV from Kubernetes alone: it must be present when building the static bundle, or it stays undefined in the browser.
const isProductionBundle = process.env.NODE_ENV === "production";

const RestaurantModules = ({ tKey = "dashboard.modules." }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modules, setModules] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [priceInterval, setPriceInterval] = useState("year"); // Default to yearly (better value)
  const restaurantId = useSelector((state) => state.user.restaurantId);

  const { request: fetchModulesRequest, loading: modulesLoading } =
    useApi(getAvailableModules);
  const { request: fetchSubscriptionsRequest, loading: subscriptionsLoading } =
    useApi(getRestaurantSubscriptions);
  const { request: createCheckoutRequest, loading: checkoutLoading } = useApi(
    createCheckoutSession,
  );
  const { request: cancelRequest, loading: cancelLoading } =
    useApi(cancelSubscription);
  const { request: reactivateRequest, loading: reactivateLoading } = useApi(
    reactivateSubscription,
  );
  const { request: portalRequest, loading: portalLoading } =
    useApi(createPortalSession);

  const fetchData = useCallback(async () => {
    try {
      const [modulesRes, subsRes] = await Promise.all([
        fetchModulesRequest(),
        fetchSubscriptionsRequest(restaurantId),
      ]);
      if (modulesRes?.data) setModules(modulesRes.data);
      if (subsRes?.data) {
        setSubscriptions(subsRes.data);
        // Update Redux so sidebar and other components reflect the change
        dispatch(
          updateRestaurantSubscriptions({
            restaurantId,
            subscriptions: subsRes.data,
          }),
        );
      }
    } catch (err) {
      toast.error(err?.message || t(`${tKey}errors.load`));
    }
  }, [
    restaurantId,
    fetchModulesRequest,
    fetchSubscriptionsRequest,
    dispatch,
    t,
    tKey,
  ]);

  useEffect(() => {
    if (restaurantId) fetchData();
  }, [fetchData, restaurantId]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");
    if (success === "true") {
      toast.success(t(`${tKey}success.subscribed`));
      setSearchParams({}, { replace: true });
      fetchData();
    } else if (canceled === "true") {
      toast.info(t(`${tKey}canceled`));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, t, tKey, fetchData]);

  const handleSubscribe = useCallback(
    async (priceId) => {
      if (isProductionBundle) return;
      try {
        const res = await createCheckoutRequest(restaurantId, priceId);
        if (res?.data?.url) window.location.href = res.data.url;
        else toast.error(t(`${tKey}errors.subscribe`));
      } catch (err) {
        toast.error(err?.message || t(`${tKey}errors.subscribe`));
      }
    },
    [restaurantId, createCheckoutRequest, t, tKey],
  );

  const handleCancel = useCallback(
    async (subscriptionId) => {
      try {
        await cancelRequest(subscriptionId);
        toast.success(t(`${tKey}success.canceled`));
        fetchData();
      } catch (err) {
        toast.error(err?.message || t(`${tKey}errors.cancel`));
      }
    },
    [cancelRequest, t, tKey, fetchData],
  );

  const handleReactivate = useCallback(
    async (subscriptionId) => {
      try {
        await reactivateRequest(subscriptionId);
        toast.success(t(`${tKey}success.reactivated`));
        fetchData();
      } catch (err) {
        toast.error(err?.message || t(`${tKey}errors.reactivate`));
      }
    },
    [reactivateRequest, t, tKey, fetchData],
  );

  const handleManageBilling = useCallback(async () => {
    try {
      const res = await portalRequest(restaurantId);
      if (res?.data?.url) window.location.href = res.data.url;
      else toast.error(t(`${tKey}errors.portal`));
    } catch (err) {
      toast.error(err?.message || t(`${tKey}errors.portal`));
    }
  }, [restaurantId, portalRequest, t, tKey]);

  const getModuleIcon = (moduleName) => {
    return moduleIcons[moduleName] || moduleIcons.default;
  };

  const getSubscriptionForModule = (moduleId) => {
    return subscriptions.find(
      (s) =>
        s.moduleId === moduleId && ["active", "trialing"].includes(s.status),
    );
  };

  const getPrices = (mod) => {
    const prices = mod.prices || [];
    const monthly = prices.find((p) => p.interval === "month");
    const yearly = prices.find((p) => p.interval === "year");
    const currency =
      yearly?.currency || monthly?.currency || "chf";
    return { monthly, yearly, currency };
  };

  const formatActivationDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const activeSubscriptions = subscriptions.filter((s) =>
    ["active", "trialing"].includes(s.status),
  );
  const availableModules = modules.filter(
    (mod) => !getSubscriptionForModule(mod.moduleId),
  );

  const isLoading = modulesLoading || subscriptionsLoading;

  if (isLoading) {
    return (
      <div className="dashboard-content flex align-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="dashboard-content ecosystem" data-tour="modules-list">
      {/* Hero header */}
      <header className="ecosystem__header">
        <h1 className="ecosystem__title">{t(`${tKey}pageTitle`)}</h1>
        <p className="ecosystem__subtitle">{t(`${tKey}pageSubtitle`)}</p>
      </header>

      {/* Available Modules - Primary conversion area */}
      {availableModules.length > 0 && (
        <section className="ecosystem__section ecosystem__section--available">
          {/* Pricing toggle */}
          <div className="ecosystem__toggle-wrapper">
            <div className="ecosystem__toggle">
              <button
                type="button"
                className={`ecosystem__toggle-btn ${priceInterval === "month" ? "ecosystem__toggle-btn--active" : ""}`}
                onClick={() => setPriceInterval("month")}
              >
                {t(`${tKey}monthly`)}
              </button>
              <button
                type="button"
                className={`ecosystem__toggle-btn ${priceInterval === "year" ? "ecosystem__toggle-btn--active" : ""}`}
                onClick={() => setPriceInterval("year")}
              >
                {t(`${tKey}yearly`)}
                <span className="ecosystem__toggle-badge">
                  {t(`${tKey}yearlySavings`)}
                </span>
              </button>
            </div>
          </div>

          {/* Module cards */}
          <div className="ecosystem__grid">
            {availableModules.map((mod) => {
              const { monthly, yearly, currency } = getPrices(mod);
              const currentPrice = priceInterval === "year" ? yearly : monthly;
              const currencyLabel = (currency || "chf").toUpperCase();

              return (
                <article key={mod.moduleId} className="ecosystem__card">
                  {/* Card header with icon */}
                  <div className="ecosystem__card-header">
                    <span className="ecosystem__card-icon">
                      <FontAwesomeIcon icon={getModuleIcon(mod.name)} />
                    </span>
                    <h2 className="ecosystem__card-title">
                      {t(`${tKey}module.${mod.name}.title`, {
                        defaultValue: mod.name,
                      })}
                    </h2>
                    {t(`${tKey}module.${mod.name}.description`, {
                      defaultValue: "",
                    }) && (
                      <p className="ecosystem__card-desc">
                        {t(`${tKey}module.${mod.name}.description`)}
                      </p>
                    )}
                  </div>

                  {/* Pricing */}
                  {currentPrice && (
                    <div className="ecosystem__card-pricing">
                      <span className="ecosystem__card-price">
                        {(currentPrice.amountCents / 100).toFixed(0)}
                        <span className="ecosystem__card-currency">
                          {" "}
                          {currencyLabel}
                        </span>
                      </span>
                      <span className="ecosystem__card-period">
                        {priceInterval === "month"
                          ? t(`${tKey}perMonth`)
                          : t(`${tKey}perYear`)}
                      </span>
                      {priceInterval === "year" && monthly && (
                        <span className="ecosystem__card-savings">
                          {t(`${tKey}insteadOf`, {
                            amount: ((monthly.amountCents * 12) / 100).toFixed(
                              0,
                            ),
                          })}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Features */}
                  <ul className="ecosystem__card-features">
                    {[1, 2, 3, 4].map((i) => {
                      const featureKey = `${tKey}features.${mod.name}.feature${i}`;
                      const feature = t(featureKey, { defaultValue: "" });
                      if (!feature || feature === featureKey) return null;
                      return (
                        <li key={i} className="ecosystem__card-feature">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="ecosystem__card-feature-icon"
                          />
                          {feature}
                        </li>
                      );
                    })}
                  </ul>

                  {/* CTA */}
                  {currentPrice && (
                    <Button
                      variant="danger"
                      className="ecosystem__card-cta"
                      onClick={() => handleSubscribe(currentPrice.priceId)}
                      disabled={checkoutLoading || isProductionBundle}
                      block
                    >
                      {t(
                        isProductionBundle
                          ? `${tKey}availableSoon`
                          : `${tKey}subscribe`,
                      )}
                      {!isProductionBundle && (
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                      )}
                    </Button>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* No modules available */}
      {availableModules.length === 0 && activeSubscriptions.length === 0 && (
        <Alert variant="pastel-info">{t(`${tKey}noModules`)}</Alert>
      )}

      {/* Active Modules - Secondary, compact section */}
      {activeSubscriptions.length > 0 && (
        <section className="ecosystem__section ecosystem__section--active">
          <div className="ecosystem__active-header">
            <h2 className="ecosystem__active-title">
              {t(`${tKey}activeModules`)}
            </h2>
            <Button
              variant="primary"
              size="xs"
              onClick={handleManageBilling}
              disabled={portalLoading}
            >
              {t(`${tKey}manageBilling`)}
            </Button>
          </div>

          <div className="ecosystem__active-list">
            {activeSubscriptions.map((sub) => (
              <div key={sub.subscriptionId} className="ecosystem__active-item">
                <span className="ecosystem__active-icon">
                  <FontAwesomeIcon icon={getModuleIcon(sub.module?.name)} />
                </span>
                <div className="ecosystem__active-info">
                  <span className="ecosystem__active-name">
                    {t(`${tKey}module.${sub.module?.name}.title`, {
                      defaultValue: sub.module?.name,
                    })}
                  </span>
                  <span className="ecosystem__active-meta">
                    {sub.cancelAtPeriodEnd ? (
                      <span className="text-warning">
                        {t(`${tKey}canceledAt`)}{" "}
                        {formatActivationDate(sub.currentPeriodEnd)}
                      </span>
                    ) : (
                      <span className="text-success">
                        {t(`${tKey}status.active`)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="ecosystem__active-actions">
                  {sub.cancelAtPeriodEnd ? (
                    <Button
                      variant="success"
                      size="xs"
                      onClick={() => handleReactivate(sub.subscriptionId)}
                      disabled={reactivateLoading}
                    >
                      {t(`${tKey}reactivate`)}
                    </Button>
                  ) : (
                    <button
                      className="ecosystem__active-cancel"
                      onClick={() => handleCancel(sub.subscriptionId)}
                      disabled={cancelLoading}
                    >
                      {t(`${tKey}cancel`)}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty state for active modules when there are available ones */}
      {activeSubscriptions.length === 0 && availableModules.length > 0 && (
        <p className="ecosystem__empty-hint">{t(`${tKey}noActiveModules`)}</p>
      )}
    </div>
  );
};

export default RestaurantModules;
